import React, { useCallback, useRef, useState } from 'react';
import { Menu } from '@teetsh/app/src/components/Menu';
import { Parcours } from '@teetsh/app/src/containers/Livrets/types/livret';
import {
  PlusIcon as Plus,
  TrashIcon as Trash,
} from '@heroicons/react/24/outline';
import _debounce from 'lodash-es/debounce';
import Textarea from '@teetsh/app/src/components/Form/Textarea';
import {
  removeItemAtIndex,
  replaceItemAtIndex,
} from '@teetsh/app/src/utils/listMethods';
import Button from '@teetsh/app/src/components/Buttons/Button';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

interface Props {
  value: Parcours[];
  onChange?: (p: Parcours[]) => void;
  readonly?: boolean;
}

interface ParcoursNomenclature {
  code: string;
  label: string;
}

const allParcoursNomenclature: ParcoursNomenclature[] = [
  {
    code: 'PAR_CIT',
    label: 'Parcours citoyen',
  },
  {
    code: 'PAR_ART',
    label: "Parcours d'éducation artistique et culturelle",
  },
  {
    code: 'PAR_SAN',
    label: 'Parcours éducatif de santé',
  },
];

function getRemainingParcoursNomenclature(
  parcours: Parcours[],
): ParcoursNomenclature[] {
  return allParcoursNomenclature.filter(
    p => !parcours.find(par => par.code === p.code),
  );
}

export function ParcoursList(props: Props) {
  const { onChange, readonly } = props;
  const { t } = useTranslation(['carnet', 'common']);
  const [value, setValue] = useState<Parcours[]>(props.value);
  const debouncedSave = useCallback(
    _debounce(nextValue => onChange && onChange(nextValue), 500),
    [onChange],
  );
  const remainingParcoursNomenclature = getRemainingParcoursNomenclature(value);
  const handleChange = nextValue => {
    setValue(nextValue);
    debouncedSave(nextValue);
  };
  const textareaRef = useRef();
  return (
    <div>
      {value.length > 0 && (
        <div css={[tw`text-xl mb-2`]}>
          {t('carnet:livret.projetMiseEnOeuvre')}
        </div>
      )}
      {(value || []).map((p, i) => {
        const matchingNomenclature = allParcoursNomenclature.find(
          nom => nom.code === p.code,
        );
        return (
          <div key={p.code} className="group" tw="mb-2 relative">
            {!readonly && (
              <div tw="absolute top-0 right-0 group-hover:block hidden bg-white rounded-lg">
                <Button
                  minimal
                  icon={Trash}
                  onClick={() => handleChange(removeItemAtIndex(value, i))}
                  intent="danger"
                />
              </div>
            )}
            <div css={[!readonly && tw`mb-1`, tw`font-bold`]}>
              {matchingNomenclature?.label}
            </div>
            {readonly ? (
              <div tw="whitespace-pre-line">{p.comment || ''}</div>
            ) : (
              <div tw="-ml-1">
                <Textarea
                  inputRef={textareaRef as any}
                  minRows={1}
                  maxLength={600}
                  removeHandle={!onChange}
                  aria-label={`Commentaire sur le parcours ${p.code}`}
                  value={p.comment || ''}
                  withCount
                  onChange={e =>
                    handleChange(
                      replaceItemAtIndex(
                        value,
                        {
                          code: p.code,
                          comment: e.currentTarget.value,
                        },
                        i,
                      ),
                    )
                  }
                />
              </div>
            )}
          </div>
        );
      })}
      {!readonly && (
        <div css={[tw`text-center`, value.length > 0 && tw`mb-4`]}>
          <Menu large={value.length === 0}>
            <Menu.Button
              icon={Plus}
              extralarge={value.length === 0}
              minimal={value.length > 0}
              fill
            >
              {t('carnet:livret.addParcours')}
            </Menu.Button>
            <Menu.Items>
              {remainingParcoursNomenclature.map(p => (
                <Menu.Item
                  key={p.code}
                  onClick={() => {
                    handleChange([...value, { code: p.code }]);
                    setTimeout(() => {
                      if (textareaRef?.current) {
                        (textareaRef.current as any)?.focus();
                      }
                    }, 100);
                  }}
                  text={p.label}
                />
              ))}
            </Menu.Items>
          </Menu>
        </div>
      )}
    </div>
  );
}
